import type { ReactNode } from 'react';
import { Paper, Stack, Text, Group } from '../../../core';
import { faFileInvoiceDollar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { surfaceHeaderProps } from '../SurfaceHeader';

export interface CreateQuoteProps {
  readonly accountName: string;
  /** Submission the quotes were created from, e.g. "SUB-123456". */
  readonly submissionNumber?: string;
  /** One CreateQuoteRow per quote created this turn. */
  readonly children: ReactNode;
}

/**
 * Create Quote — the quotes the agent created from a submission, one row each.
 * Unlike CopyQuote, every row is already a real quote; rows only track its
 * rating lifecycle (rating → ready / indication / referred / declined).
 */
export function CreateQuote({
  accountName,
  submissionNumber,
  children,
}: CreateQuoteProps) {
  return (
    <Paper
      radius="md"
      p="lg"
      withBorder
      style={{
        width: '100%',
        borderColor: 'var(--mantine-color-blue-2)',
      }}
    >
      <Group gap="xs" {...surfaceHeaderProps('lg', 'sm')}>
        <FontAwesomeIcon
          icon={faFileInvoiceDollar}
          style={{
            color: 'var(--mantine-color-blue-6)',
            width: 18,
            height: 18,
          }}
        />
        <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
          CREATE QUOTE
        </Text>
        <Text fz={13} c="dimmed">
          {accountName}
        </Text>
      </Group>

      {submissionNumber && (
        <Text fz={12} c="dimmed" mb="lg">
          {`From submission ${submissionNumber}`}
        </Text>
      )}

      <Stack gap="sm" mt={submissionNumber ? undefined : 'lg'}>
        {children}
      </Stack>
    </Paper>
  );
}
